"use client";

import classnames from "classnames";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import dynamic from "next/dynamic";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import useShowNewHeader from "@hooks/use-show-new-header";

import { loginCommonParams } from "@services/auth";
import { useAuth0V2 } from "@stadium/services/auth0";
import { EventLocationProvider } from "@client/wrappers";

import ImpersonationBanner from "@stadium/ui/src/components/impersonation-banner";
import SwagFooterCart from "@components/create-swag/footer-cart";

import OpenOrderProvider from "@contexts/open-order-context";
import PreviewModeProvider, { usePreviewModeContext } from "@contexts/preview-mode-context";
import useGuestUserLogin from "@hooks/guest-user-login/use-guest-user-login";

import { getAppTheme, getIsStoreDarkTheme, getStoreCatalogLogo } from "@selectors/store";

import { resetCache } from "@slices/index";

import Footer from "@components/store/footer";
import DefaultHeader from "@components/store/navigation";
import CreateShopBanner from "@components/store/navigation/create-shop-banner";
import BreadCrumbs from "@components/swagmagic/bread-crumbs";
import SwagmagicHeader from "@components/swagmagic/header";

import GuideTourProvider, { useGuideTourContext } from "@contexts/guide-tour";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { useStoreContext } from "@components/store/provider";
import { useCreateShopContext } from "@contexts/create-shop-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import { getCreateShopTourStepsData } from "@helpers/shop-guide-tour-steps";
import {
  getFooterCartTourStepsData,
  FOOTER_CART_GUIDE_TOUR_USER_CONFIG,
} from "@helpers/footer-cart-guide-tour-steps";

import { FOOTER_PLATFORM } from "@constants/app-defaults";
import { KIT_COUNTRIES_ISO_LIST } from "@constants/country";
import { DEFAULT_APP_THEME } from "@constants/store";
import { GLOBAL_COUNTRY_ISO } from "@stadium/helpers/country/constants";

import MultiLevelHeader from "@components/store/navigation/multi-level-header";

import { META_QUERY_PARAM_KEY } from "@constants/routes";
import { HEADER_EVENT_LOCATION } from "@stadium/shared/src/constants/analytics/header";

import styles from "./main.module.scss";

const DemoModeBar = dynamic(() => import("@components/store/demo-mode-bar"));
const KitHeader = dynamic(() => import("@components/store/navigation/kit"));
const StadiumFooter = dynamic(() => import("@components/stadium-footer"), { ssr: false });
const SwagmagicFooter = dynamic(() => import("@components/swagmagic/footer"));

const Header = ({ showKitHeader = false, additionalData, ...rest }) => {
  const showNewHeader = useShowNewHeader();

  const { isPreviewCatalogMode, isNewSwagmagicHost } = useStoreContext();
  if (isNewSwagmagicHost) {
    return (
      <SwagmagicHeader
        dynamicNavData={additionalData?.dynamicNavData}
        headerData={additionalData?.headerData}
      />
    );
  }
  if (showNewHeader && !isPreviewCatalogMode) {
    return <MultiLevelHeader {...rest} />;
  }

  if (showKitHeader) {
    return <KitHeader {...rest} />;
  }
  return <DefaultHeader {...rest} />;
};

const LayoutFooter = ({ additionalData }) => {
  const { isNewSwagmagicHost, isPreviewCatalogMode } = useStoreContext();
  if (isNewSwagmagicHost) {
    return <SwagmagicFooter footerData={additionalData?.footerData} />;
  }
  if (isPreviewCatalogMode) {
    return <StadiumFooter platform={FOOTER_PLATFORM} branding={false} showWeglotDropDown={true} />;
  }
  return (
    <div className={styles.footerWrapper}>
      <Footer />
    </div>
  );
};

const StoreLayoutMain = ({ children, additionalData }) => {
  const showNewHeader = useShowNewHeader();

  const isStoreDarkTheme = useSelector(getIsStoreDarkTheme);

  const router = useRouter();
  const { pathname, query } = router;

  const { user, isAuthenticated, loginWithRedirect, isLoading } = useAuth0V2();
  const dispatch = useDispatch();

  const {
    storeNumber,
    isKitShop,
    isPreviewCatalogMode,
    isKitRedemption,
    showHeader,
    showFooter,
    countryCode,
    storeCode: slug,
    isNewRoute,
    lang,
    isFetchingMenu,
    showBreadCrumbs,
  } = useStoreContext();

  const { isNewShopCreationFlow, currentStoreNumber, setCurrentStoreNumber } =
    useCreateShopContext();

  const { isSwagCreationShopFlow } = useCreateSwagContext();

  const theme = useSelector(getAppTheme);
  const appTheme = theme || DEFAULT_APP_THEME;

  const { previewMode } = usePreviewModeContext();
  const { renderTour } = useGuideTourContext();

  const { renderCartMergeConfirmationModal, isOpen: isOpencartMergeConfirmationModal } =
    useGuestUserLogin();

  useEffect(() => {
    const hasBundleSupportedCountry = KIT_COUNTRIES_ISO_LIST.includes(countryCode?.toUpperCase());

    if (isKitShop && !hasBundleSupportedCountry && !isKitRedemption) {
      router.push({
        pathname,
        query: {
          ...omit(query, META_QUERY_PARAM_KEY),
          countryCode: isNewRoute
            ? `${GLOBAL_COUNTRY_ISO.toLowerCase()}-${lang}`
            : GLOBAL_COUNTRY_ISO.toLowerCase(),
        },
      });
    }
  }, [isKitShop]);

  // TODO: COUNTRY CODE TO BE SET US FOR LOCKER SHOPS
  useEffect(() => {
    // //Ask the user to log in in the case of bundle shop
    if (!isLoading && isKitShop && !isAuthenticated && !isKitRedemption) {
      loginWithRedirect(loginCommonParams());
      return;
    }
  }, [router, slug, storeNumber, isAuthenticated, isLoading]);

  useEffect(() => {
    const body = document.body;
    body.classList.add(appTheme);
    return () => {
      body.classList.remove(appTheme);
    };
  }, [appTheme]);

  useEffect(() => {
    if (storeNumber && currentStoreNumber !== storeNumber) {
      dispatch(resetCache()); //to reset the STORE_DEPENDENT_STATES while traversing between shops
      setCurrentStoreNumber(storeNumber);
    }
  }, [storeNumber]);

  return (
    <OpenOrderProvider storeNumber={storeNumber}>
      <ImpersonationBanner currentUsername={user?.name} />
      {!showNewHeader && isNewShopCreationFlow && <CreateShopBanner />}
      <div
        id="shop-main-wrapper"
        className={classnames({
          "preview-mode": previewMode,
          [styles.catalogWrapper]: isPreviewCatalogMode,
          "dark-theme": !isPreviewCatalogMode && isStoreDarkTheme,
        })}
      >
        {showHeader && (
          <EventLocationProvider location={HEADER_EVENT_LOCATION}>
            <Header
              showKitHeader={isKitShop && !isKitRedemption}
              isFetchingMenu={isFetchingMenu}
              additionalData={additionalData}
            />
          </EventLocationProvider>
        )}
        {showBreadCrumbs && <BreadCrumbs />}
        {children}
        {showFooter && <LayoutFooter additionalData={additionalData} />}
        {isOpencartMergeConfirmationModal && renderCartMergeConfirmationModal()}
      </div>
      {renderTour()}
      {previewMode && showNewHeader && <DemoModeBar />}
      {isSwagCreationShopFlow && <SwagFooterCart />}
    </OpenOrderProvider>
  );
};

const StoreLayout = ({ children, additionalData }) => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { storeNumber } = useStoreContext();

  const { isNewShopCreationFlow } = useCreateShopContext();
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const storeCatalogLogo = useSelector(getStoreCatalogLogo);

  const guideTourSteps = useMemo(() => {
    if (isNewShopCreationFlow) {
      const createShopTourSteps = getCreateShopTourStepsData({
        isShopLogoAdded: !isEmpty(storeCatalogLogo),
        isMobileDevice,
      });

      const { step1, step2, step3, step5 } = createShopTourSteps;
      return {
        step1,
        step2,
        step3,
        step5: {
          ...step5,
          id: 4,
        },
      };
    }

    if (isSwagCreationShopFlow) {
      return getFooterCartTourStepsData();
    }

    return {};
  }, [isNewShopCreationFlow, isSwagCreationShopFlow, storeCatalogLogo, isMobileDevice]);

  const guidedTourOptions = useMemo(
    () => ({
      guideTourSteps,
      ...(isSwagCreationShopFlow && {
        userConfigTourPersistence: FOOTER_CART_GUIDE_TOUR_USER_CONFIG,
      }),
    }),
    [guideTourSteps, isSwagCreationShopFlow],
  );

  return (
    <PreviewModeProvider storeNumber={storeNumber}>
      <GuideTourProvider guidedTourOptions={guidedTourOptions}>
        <StoreLayoutMain additionalData={additionalData}>{children}</StoreLayoutMain>
      </GuideTourProvider>
    </PreviewModeProvider>
  );
};

export default StoreLayout;
